/**
 * Role Stream Parser
 * Parses streaming staffed role content and creates compartments
 */

class RoleParser {
  constructor() {
    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PUBLIC API (used by App.js)
  // =================================================================

  processStreamContent(params) {
    const { content, section } = params;

    if (section !== "staffing") {
      return {
        isSuccessful: false,
        content: []
      };
    }

    if (!content) {
      return {
        isSuccessful: false,
        content: []
      };
    }

    this.state.processingState = 'processing';
    this.state.buffer += content;

    try {
      this.parseStaffedRoles(this.state.buffer);

      if (this.state.compartments.length > 0) {
        this.state.processingState = 'completed';
        return {
          isSuccessful: true,
          content: this.getValidCompartments()
        };
      }

      this.state.processingState = 'idle';
      return {
        isSuccessful: false,
        content: []
      };

    } catch (error) {
      console.error('Role Parser: Error processing content:', error);
      this.state.processingState = 'error';
      return {
        isSuccessful: false,
        content: []
      };
    }
  }

  resetState() {
    console.log('🔄 Role Parser: Resetting state');

    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PARSING LOGIC
  // =================================================================

  parseStaffedRoles(buffer) {
    const beforeCount = this.state.compartments.length;
    const matches = [...buffer.matchAll(/NEWSTAFFEDROLE/g)];
    const currentCount = matches.length;

    if (currentCount > beforeCount) {
      // Create new compartments
      for (let i = beforeCount; i < currentCount; i++) {
        this.createCompartment(i + 1);
      }
      this.updateCompartmentContent(buffer);
    } else if (this.state.compartments.length > 0) {
      // Stream to last compartment
      this.streamToLastCompartment(buffer);
    }
  }

  createCompartment(number) {
    const compartment = {
      id: `staffedrole_${number}`,
      number: number,
      title: `Staffed Role ${number}`,
      content: "",
      isExpanded: true
    };

    this.state.compartments.push(compartment);
    console.log(`🆕 Role Parser: Compartment ${number} created`);
  }

  updateCompartmentContent(fullBuffer) {
    const sections = fullBuffer.split('NEWSTAFFEDROLE');

    sections.slice(1).forEach((section, index) => {
      if (index < this.state.compartments.length) {
        const existingCompartment = this.state.compartments[index];

        if (!this.isValidCompartment(existingCompartment)) {
          console.error(`❌ Invalid compartment at index ${index}:`, existingCompartment);
          return;
        }

        let cleanContent = section.trim();
        cleanContent = cleanContent.replace(/ENDOFSTAFFING.*$/s, '').trim();

        // Extract role title from content
        let title = this.extractRoleTitle(cleanContent);
        if (!title) {
          // Fallback to first line if role title not found
          const lines = section.trim().split('\n');
          const rawTitle = (lines[0] && lines[0].trim()) || `Staffed Role ${index + 1}`;
          title = this.stripMarkdown(rawTitle);
        }

        // Extract assignment type and append to title
        const assignmentType = this.extractAssignmentType(cleanContent);
        if (assignmentType) {
          title = `${title} - ${assignmentType}`;
        }

        const updatedCompartment = {
          ...existingCompartment,
          title: title,
          content: cleanContent
        };

        if (this.isValidCompartment(updatedCompartment)) {
          this.state.compartments[index] = updatedCompartment;
        }
      }
    });
  }

  streamToLastCompartment(buffer) {
    if (this.state.compartments.length === 0) return;

    const lastIndex = this.state.compartments.length - 1;
    const existingCompartment = this.state.compartments[lastIndex];

    if (!this.isValidCompartment(existingCompartment)) {
      console.error(`❌ Invalid last compartment at index ${lastIndex}:`, existingCompartment);
      return;
    }

    const sections = buffer.split('NEWSTAFFEDROLE');

    if (sections.length > lastIndex + 1) {
      const lastSection = sections[lastIndex + 1];
      let cleanContent = lastSection.trim();
      cleanContent = cleanContent.replace(/ENDOFSTAFFING.*$/s, '').trim();

      // Extract role title from content
      let title = this.extractRoleTitle(cleanContent);
      if (!title) {
        // Fallback to first line or existing title if role title not found
        const lines = lastSection.trim().split('\n');
        const rawTitle = (lines[0] && lines[0].trim()) || existingCompartment.title;
        title = this.stripMarkdown(rawTitle);
      }

      // Extract assignment type and append to title
      const assignmentType = this.extractAssignmentType(cleanContent);
      if (assignmentType) {
        title = `${title} - ${assignmentType}`;
      }

      const updatedCompartment = {
        ...existingCompartment,
        title: title,
        content: cleanContent
      };

      if (this.isValidCompartment(updatedCompartment)) {
        this.state.compartments[lastIndex] = updatedCompartment;
      }
    }
  }

  // =================================================================
  // UTILITY & VALIDATION
  // =================================================================

  extractRoleTitle(content) {
    if (!content) return null;

    // Look for "Role Title:" with optional markdown formatting
    const roleTitleMatch = content.match(/\*\*Role Title:\*\*\s*(.+?)(?:\n|$)/i) ||
                          content.match(/Role Title:\s*(.+?)(?:\n|$)/i);

    if (roleTitleMatch && roleTitleMatch[1]) {
      return roleTitleMatch[1].trim();
    }

    return null;
  }

  extractAssignmentType(content) {
    if (!content) return null;

    // Check for Primary Assignment
    if (content.includes('Primary Assignment:')) {
      return 'Primary Assignment';
    }

    // Check for Alternative Assignment
    if (content.includes('Alternative Assignment:')) {
      return 'Alternative Assignment';
    }

    return null;
  }

  stripMarkdown(text) {
    if (!text) return text;
    return text
      .replace(/\*\*/g, '')  // Remove bold **
      .replace(/\*/g, '')    // Remove italic *
      .replace(/__/g, '')    // Remove bold __
      .replace(/_/g, '')     // Remove italic _
      .replace(/`/g, '')     // Remove code `
      .replace(/^#+\s*/g, '') // Remove headers #
      .trim();
  }

  isValidCompartment(compartment) {
    return compartment &&
           typeof compartment.id === 'string' &&
           compartment.id.length > 0 &&
           typeof compartment.number === 'number' &&
           (typeof compartment.title === 'string' || compartment.title === undefined);
  }

  getValidCompartments() {
    return this.state.compartments.filter(compartment => {
      const isValid = this.isValidCompartment(compartment);
      if (!isValid) {
        console.warn('Invalid compartment filtered out:', compartment);
      }
      return isValid;
    });
  }
}

export default RoleParser;
